import { useState, useCallback, useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import MuiIconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  MODAL_MEDIA_ARIA_LABEL,
  DELETE_CONTENT_CONFIRMATION_TITLE,
  DELETE_CONTENT_CONFIRMATION_DESCRIPTION,
  BUTTON_DELETE_CONTENT,
  BUTTON_APPROVE_CONTENT,
  BUTTON_REJECT_CONTENT,
  REQUEST_REJECT_CONFIRMATION_DESCRIPTION,
  REQUEST_APPROVE_CONFIRMATION_DESCRIPTION,
  READY,
  WARNING,
} from "../../i18n/keysTranslations";
import { getExtensionFile } from "../../utils/string";
import { useTranslationApp } from "../../lib/i18next";
import { EDITOR_ASSETS_LIST_ROOT, VIDEO_FORMATS } from "../../utils/constants";
import {
  PNG_ASSET_BACKDROP_SX,
  isPngAssetExtension,
} from "../../utils/assetGalleryUtils";
import ModalBasicLayout from "./ModalBasicLayout";
import CardMedia from "../Media/CardMedia";
import SlideTrack from "../Common/SlideTrack";
import { useSlideLock } from "../../hooks/useSlideLock";
import {
  startGetAwsDownloadUrl,
  startDownloadCarouselZip,
  startDeleteContentFromListAndStorage,
} from "../../actions/user";
import { getCarouselUrls } from "../../utils/carouselUtils";
import { useDispatchApp } from "../../lib/redux";
import IconButton from "../Buttons/IconButton";
import Button from "../Buttons/Button";
import FormatMenuButton from "../Gallery/FormatMenuButton";
import { useMediaDimensions } from "../../hooks/useMediaDimensions";
import { Share2 } from "lucide-react";
import ShareModalMultiple from "../Gallery/ShareModalMultiple/ShareModalMultiple";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import ContentApprovalControls from "../ContentApproval/ContentApprovalControls";

import { LaunchError } from "../../utils/errors";

const ModalMedia = ({
  modalOpen = false,
  onCloseModal,
  url,
  customActions,
  onEditWithCanva,
  onEditWithIA,
  dimensions,
  showEditButton = true,
  showDownloadButton = true,
  showShareButton = true,
  showDeleteButton = true,
  showApproveButton = false,
  showRejectButton = false,
  item,
  approveRejectData = null,
  onApproveContent = () => {},
  onRejectContent = () => {},
  onDeleteSuccess = () => {},
  onResizeFormat = () => {},
  onAnimateImage = () => {},
  isFromCarousel = false,
  onDelete = null,
  listRoot = "contentList",
  isPicker = false,
  contentApprovalData = null,
  onOpenContentApprovalReview = () => {},
  onShareSuccess,
  disableRightClickSave = false,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRejectingContent, setIsRejectingContent] = useState(false);
  const [isApprovingContent, setIsApprovingContent] = useState(false);

  const isCarousel =
    isFromCarousel ||
    (item?.isCarousel &&
      item?.carouselImages &&
      item.carouselImages.length > 1);
  const carouselImages = useMemo(() => {
    return isCarousel ? item?.carouselImages || [] : [];
  }, [isCarousel, item?.carouselImages]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(() =>
    item?._initialCarouselIndex !== undefined ? item._initialCarouselIndex : 0
  );

  const currentUrl = useMemo(() => {
    if (isCarousel && carouselImages.length > 0) {
      const currentImage = carouselImages[currentCarouselIndex];
      return currentImage?.url || currentImage || url;
    }
    return url;
  }, [isCarousel, carouselImages, currentCarouselIndex, url]);

  const extension = getExtensionFile(currentUrl);
  const showPngAssetBackdrop =
    listRoot === EDITOR_ASSETS_LIST_ROOT && isPngAssetExtension({ extension });

  useEffect(() => {
    if (modalOpen && isCarousel) {
      const initialIndex =
        item?._initialCarouselIndex !== undefined
          ? item._initialCarouselIndex
          : 0;
      setCurrentCarouselIndex(initialIndex);
    }
  }, [modalOpen, isCarousel, item?.id, item?._initialCarouselIndex]);

  const { isSlideLocked, lockSlide } = useSlideLock();

  const onPrev = useCallback(() => {
    if (isCarousel && currentCarouselIndex > 0 && lockSlide()) {
      setCurrentCarouselIndex((prev) => prev - 1);
    }
  }, [isCarousel, currentCarouselIndex, lockSlide]);

  const onNext = useCallback(() => {
    if (
      isCarousel &&
      currentCarouselIndex < carouselImages.length - 1 &&
      lockSlide()
    ) {
      setCurrentCarouselIndex((prev) => prev + 1);
    }
  }, [isCarousel, currentCarouselIndex, carouselImages.length, lockSlide]);

  const isFirstImage = currentCarouselIndex === 0;
  const isLastImage = currentCarouselIndex === carouselImages.length - 1;

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const { dimensions: calculatedDimensions } = useMediaDimensions(currentUrl);

  const finalDimensions = dimensions || calculatedDimensions;

  const onEditImageWithCanva = () => {
    if (onEditWithCanva) {
      onEditWithCanva({
        url: currentUrl,
        dimensions: finalDimensions,
        ...(isCarousel && {
          carouselIndex: currentCarouselIndex,
          isCarousel: true,
          itemId: item.id,
        }),
      });
    }
  };

  const onEditImageWithIA = () => {
    if (onEditWithIA) {
      onEditWithIA({
        url: currentUrl,
        dimensions: finalDimensions,
        itemId: item.id,
        ...(isCarousel && {
          carouselIndex: currentCarouselIndex,
          isCarousel: true,
        }),
      });
    }
  };

  const onAnimateImageWithIA = () => {
    if (onAnimateImage) {
      onAnimateImage();
    }
  };

  const sxIconButton = {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    width: 40,
    height: 40,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
  };

  const onClickShare = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsShareModalOpen(true);
  }, []);

  const onDownloadMedia = useCallback(
    async (e) => {
      if (e) e.stopPropagation();
      setIsFetching(true);

      try {
        if (isCarousel && carouselImages.length > 0) {
          const urls = getCarouselUrls(item);
          await dispatch(startDownloadCarouselZip({ urls }));
        } else {
          await dispatch(startGetAwsDownloadUrl(currentUrl));
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "onAnimateImageWithIA",
            capture: "exception",
          })
        );
        console.error("Error al descargar el archivo:", error);
      } finally {
        setIsFetching(false);
      }
    },
    [isCarousel, carouselImages.length, item, currentUrl, dispatch]
  );

  const onDeleteContent = useCallback(async () => {
    const result = await ConfirmAlert({
      title: t(DELETE_CONTENT_CONFIRMATION_TITLE),
      text: t(DELETE_CONTENT_CONFIRMATION_DESCRIPTION),
      confirmButtonText: t(BUTTON_DELETE_CONTENT),
    });

    if (!result.isConfirmed) return;

    try {
      const contentID = item?.id;

      if (!contentID) {
        console.error("No content ID provided");
        return;
      }

      const response = await dispatch(
        startDeleteContentFromListAndStorage({
          contentID,
          listRoot,
        })
      );

      if (response) {
        onDeleteSuccess();
        onCloseModal();
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onAnimateImageWithIA",
          capture: "exception",
        })
      );
      console.error("Error deleting content:", error);
    }
  }, [dispatch, item?.id, t, onDeleteSuccess, onCloseModal, listRoot]);

  const onDeleteClick = useCallback(
    async (e) => {
      e?.stopPropagation();

      if (onDelete) {
        await onDelete();
        onCloseModal();
        return;
      }

      await onDeleteContent();
    },
    [onDelete, onCloseModal, onDeleteContent]
  );

  const onApproveClick = useCallback(async () => {
    if (!approveRejectData?.key || isApprovingContent || isRejectingContent) {
      return;
    }

    const confirmResult = await ConfirmAlert({
      title: t(WARNING),
      text: t(REQUEST_APPROVE_CONFIRMATION_DESCRIPTION),
    });
    if (!confirmResult?.isConfirmed) return;

    setIsApprovingContent(true);
    try {
      const response = await onApproveContent(approveRejectData);
      if (response?.ok) {
        await SimpleAlert({
          title: t(READY),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        onCloseModal();
      }
    } finally {
      setIsApprovingContent(false);
    }
  }, [
    approveRejectData,
    isApprovingContent,
    isRejectingContent,
    onApproveContent,
    onCloseModal,
    t,
  ]);

  const onRejectClick = useCallback(async () => {
    if (!approveRejectData?.key || isRejectingContent || isApprovingContent) {
      return;
    }

    const confirmResult = await ConfirmAlert({
      title: t(WARNING),
      text: t(REQUEST_REJECT_CONFIRMATION_DESCRIPTION),
    });
    if (!confirmResult?.isConfirmed) return;

    setIsRejectingContent(true);
    try {
      const response = await onRejectContent(approveRejectData);
      if (response?.ok) {
        await SimpleAlert({
          title: t(READY),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        onCloseModal();
      }
    } finally {
      setIsRejectingContent(false);
    }
  }, [
    approveRejectData,
    isApprovingContent,
    isRejectingContent,
    onCloseModal,
    onRejectContent,
    t,
  ]);

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        fullWidth={true}
        fullScreen
        scroll="paper"
        ariaLabel={t(MODAL_MEDIA_ARIA_LABEL)}
        sxContent={{
          p: 0,
          pl: 3,
          pb: 2,
          mt: 1,
        }}
        sxTitle={{
          py: 1,
        }}
      >
        <Box
          onContextMenu={
            disableRightClickSave
              ? (event) => event.preventDefault()
              : undefined
          }
          sx={{
            height: { xs: "70vh", sm: "80vh" },
            display: "flex",
            justifyContent: "center",
            px: 1,
            position: "relative",
            ...(disableRightClickSave && {
              "& img, & video": {
                WebkitUserDrag: "none",
                userSelect: "none",
                WebkitTouchCallout: "none",
              },
            }),
            ...(showPngAssetBackdrop && {
              ...PNG_ASSET_BACKDROP_SX,
              borderRadius: 3,
            }),
          }}
          className="modal-media-content"
        >
          {VIDEO_FORMATS.includes(extension) ? (
            <CardMedia
              url={url}
              propsType={{
                controls: true,
                controlsList: "nodownload",
                autoplay: true,
                loop: true,
                muted: false,
                ...(disableRightClickSave ? { draggable: false } : {}),
              }}
              sxVideo={{
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                boxShadow: "none",
                borderRadius: 3,
              }}
            />
          ) : (
            <SlideTrack
              urls={
                isCarousel
                  ? carouselImages.map((img) => img?.url || img)
                  : [currentUrl]
              }
              index={isCarousel ? currentCarouselIndex : 0}
              sx={
                finalDimensions?.width && finalDimensions?.height
                  ? {
                      height: "100%",
                      maxWidth: "100%",
                      aspectRatio: `${finalDimensions.width} / ${finalDimensions.height}`,
                    }
                  : { width: "100%", height: "100%" }
              }
              renderSlide={(slideUrl) => (
                <Avatar
                  variant="rounded"
                  src={slideUrl}
                  alt={`Media`}
                  imgProps={
                    disableRightClickSave
                      ? {
                          draggable: false,
                          onContextMenu: (event) => event.preventDefault(),
                        }
                      : undefined
                  }
                  sx={{
                    height: "100%",
                    width: "100%",
                    bgcolor: "transparent",
                    ...(showPngAssetBackdrop ? PNG_ASSET_BACKDROP_SX : {}),
                    ".MuiAvatar-img": {
                      width: "100%",
                      objectFit: "contain",
                      objectPosition: "center",
                      ...(disableRightClickSave && {
                        WebkitUserDrag: "none",
                        userSelect: "none",
                        WebkitTouchCallout: "none",
                      }),
                    },
                  }}
                />
              )}
            />
          )}

          {/* Navigation controls para carousel */}
          {isCarousel && carouselImages.length > 1 && (
            <>
              <MuiIconButton
                onClick={onPrev}
                disabled={isFirstImage || isSlideLocked}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  "&:hover": {
                    bgcolor: isFirstImage
                      ? "rgba(0, 0, 0, 0.7)"
                      : "rgba(0, 0, 0, 0.9)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ color: "#fff !important" }} />
              </MuiIconButton>
              <MuiIconButton
                onClick={onNext}
                disabled={isLastImage || isSlideLocked}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  "&:hover": {
                    bgcolor: isLastImage
                      ? "rgba(0, 0, 0, 0.7)"
                      : "rgba(0, 0, 0, 0.9)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <ChevronRightIcon sx={{ color: "#fff !important" }} />
              </MuiIconButton>

              {/* Position indicator */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#fff !important",
                }}
              >
                {currentCarouselIndex + 1} / {carouselImages.length}
              </Box>
            </>
          )}
        </Box>
        <Box
          sx={{
            height: { xs: "22vh", sm: "13vh" },
            display: "flex",
            alignItems: { xs: "flex-end", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
            px: 2,
            pb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
              width: "100%",
            }}
          >
            {showEditButton && !VIDEO_FORMATS.includes(extension) ? (
              <FormatMenuButton
                url={currentUrl}
                onResizeFormat={
                  isCarousel
                    ? () => {}
                    : ({
                        format,
                        formats,
                        width,
                        height,
                        onCreditsConfirmed,
                      }) =>
                        onResizeFormat({
                          format,
                          formats,
                          width,
                          height,
                          onCreditsConfirmed,
                        })
                }
                onEditWithCanva={onEditImageWithCanva}
                onEditWithIA={onEditImageWithIA}
                onAnimateImage={onAnimateImageWithIA}
                hideResizeOptions={isCarousel}
                showAnimate={!isCarousel}
                sx={sxIconButton}
              />
            ) : null}

            {showDownloadButton && (
              <IconButton
                onClick={onDownloadMedia}
                type="icon"
                loaderColor="white"
                loading={isFetching}
                sx={sxIconButton}
              >
                <DownloadIcon sx={{ color: "white", fill: "white" }} />
              </IconButton>
            )}

            {showShareButton && !isPicker && (
              <IconButton onClick={onClickShare} sx={sxIconButton} size="small">
                <Share2 size={18} color="white" />
              </IconButton>
            )}

            {showDeleteButton && !isPicker && (item?.id || onDelete) && (
              <IconButton onClick={onDeleteClick} type="icon" sx={sxIconButton}>
                <DeleteIcon sx={{ color: "white", fill: "white" }} />
              </IconButton>
            )}

            {showApproveButton && (
              <Button
                color="secondary"
                size="small"
                loading={isApprovingContent}
                disabled={
                  !approveRejectData?.key ||
                  isRejectingContent ||
                  isApprovingContent
                }
                onClick={onApproveClick}
              >
                {t(BUTTON_APPROVE_CONTENT)}
              </Button>
            )}

            {showRejectButton && (
              <Button
                color="secondary"
                size="small"
                loading={isRejectingContent}
                disabled={
                  !approveRejectData?.key ||
                  isApprovingContent ||
                  isRejectingContent
                }
                onClick={onRejectClick}
              >
                {t(BUTTON_REJECT_CONTENT)}
              </Button>
            )}

            {contentApprovalData ? (
              <ContentApprovalControls
                approvalData={contentApprovalData}
                showEnableButton={false}
                onOpenReview={onOpenContentApprovalReview}
              />
            ) : null}

            {customActions}
          </Box>
        </Box>
      </ModalBasicLayout>
      {isShareModalOpen && (
        <ShareModalMultiple
          modalOpen={isShareModalOpen}
          onCloseModal={() => setIsShareModalOpen(false)}
          item={
            isCarousel
              ? item
              : {
                  url: currentUrl,
                  id: item?.id,
                  contentID: item?.contentID || item?.id,
                }
          }
          onShareSuccess={onShareSuccess}
        />
      )}
    </>
  );
};

export default ModalMedia;
